import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'

/**
 * Context value for audio unlock state and actions
 */
interface AudioUnlockContextValue {
  /** Whether audio has been unlocked for iOS Safari */
  isUnlocked: boolean
  /** Function to unlock audio (should be called on user gesture) */
  unlock: () => Promise<void>
}

const AudioUnlockContext = createContext<AudioUnlockContextValue | null>(null)

/**
 * Silent audio data URI for unlocking iOS Safari audio
 * This is a minimal valid WAV file with no audible content
 */
const SILENT_AUDIO_DATA_URI =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'

/**
 * Props for AudioUnlockProvider
 */
interface AudioUnlockProviderProps {
  children: ReactNode
}

/**
 * Provider component for iOS Safari audio unlock functionality
 *
 * iOS Safari has strict autoplay policies that require a user gesture
 * before any audio can be played. This provider manages the unlock state
 * and provides a function to unlock audio playback.
 *
 * Performance optimization:
 * - Howler.js is dynamically imported only when unlock() is called
 * - This reduces the initial bundle size for pages that don't immediately need audio
 *
 * @example
 * ```tsx
 * // In your app layout
 * function RootLayout({ children }) {
 *   return (
 *     <AudioUnlockProvider>
 *       {children}
 *     </AudioUnlockProvider>
 *   )
 * }
 *
 * // In a component that needs audio
 * function StartButton() {
 *   const { isUnlocked, unlock } = useAudioUnlock()
 *
 *   const handleClick = async () => {
 *     if (!isUnlocked) {
 *       await unlock()
 *     }
 *     // Now audio will work
 *   }
 *
 *   return <button onClick={handleClick}>Start Learning</button>
 * }
 * ```
 */
export function AudioUnlockProvider({ children }: AudioUnlockProviderProps) {
  const [isUnlocked, setIsUnlocked] = useState(false)

  const unlock = useCallback(async () => {
    // Skip if already unlocked
    if (isUnlocked) {
      return
    }

    // Dynamically import Howler.js to reduce initial bundle size
    const { Howl, Howler } = await import('howler')

    // Get Web Audio API AudioContext from Howler
    const ctx = Howler.ctx

    // Resume AudioContext if suspended (required for iOS Safari)
    if (ctx && ctx.state === 'suspended') {
      await ctx.resume()
    }

    // Play silent audio to fully unlock iOS Safari audio
    // This is necessary because some iOS versions require actual audio playback
    return new Promise<void>((resolve) => {
      const silentSound = new Howl({
        src: [SILENT_AUDIO_DATA_URI],
        volume: 0,
        onend: () => {
          setIsUnlocked(true)
          silentSound.unload()
          resolve()
        },
        onloaderror: () => {
          // Even on error, mark as unlocked since we tried
          setIsUnlocked(true)
          resolve()
        },
        onplayerror: () => {
          // Even on error, mark as unlocked since we tried
          setIsUnlocked(true)
          resolve()
        },
      })

      silentSound.play()
    })
  }, [isUnlocked])

  return (
    <AudioUnlockContext.Provider value={{ isUnlocked, unlock }}>
      {children}
    </AudioUnlockContext.Provider>
  )
}

/**
 * Hook to access audio unlock state and actions
 *
 * Must be used within an AudioUnlockProvider
 *
 * @returns Object containing isUnlocked state and unlock function
 * @throws Error if used outside of AudioUnlockProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isUnlocked, unlock } = useAudioUnlock()
 *
 *   return (
 *     <button onClick={unlock}>
 *       {isUnlocked ? 'Audio Ready' : 'Tap to Enable Audio'}
 *     </button>
 *   )
 * }
 * ```
 */
export function useAudioUnlock(): AudioUnlockContextValue {
  const context = useContext(AudioUnlockContext)

  if (!context) {
    throw new Error('useAudioUnlock must be used within AudioUnlockProvider')
  }

  return context
}
